import { useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import {
  Button,
  Description,
  FieldError,
  Form,
  Input,
  Label,
  Spinner,
  TextArea,
  TextField,
} from "@heroui/react";

import { CONTACT_FIELDS, Contacts, EMPTY_CONTACTS } from "../types";
import { useContacts, useUpdateContacts } from "../use-contacts";

import { LOCALES } from "@/features/documents/types.ts";

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactsForm() {
  const { data, isLoading } = useContacts();
  const { mutateAsync, isPending } = useUpdateContacts();

  const [savedAt, setSavedAt] = useState<string | null>(null);

  const contacts = data?.data ?? EMPTY_CONTACTS;

  const {
    control,
    handleSubmit,
    formState: { isDirty },
    reset,
  } = useForm<Contacts>({
    // Значения приходят одним запросом — форма создаётся уже заполненной,
    // поэтому ключ на контейнере ниже пересоздаёт её после загрузки.
    defaultValues: contacts,
  });

  const onSubmit: SubmitHandler<Contacts> = (values) =>
    mutateAsync(values).then((response) => {
      reset(response.data ?? values);
      setSavedAt(new Date().toLocaleTimeString("ru-RU"));
    });

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <Spinner />
      </div>
    );
  }

  return (
    <Form className="flex flex-col gap-6" onSubmit={handleSubmit(onSubmit)}>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {CONTACT_FIELDS.map((field) => (
          <Controller
            key={field.key}
            control={control}
            name={field.key}
            render={({ fieldState, field: controlled }) => (
              <TextField
                isInvalid={!!fieldState.error}
                value={controlled.value ?? ""}
                onBlur={controlled.onBlur}
                onChange={controlled.onChange}
              >
                <Label>{field.label}</Label>
                <Input ref={controlled.ref} placeholder={field.placeholder} />
                {field.hint && <Description>{field.hint}</Description>}
                <FieldError>{fieldState.error?.message}</FieldError>
              </TextField>
            )}
            rules={{
              required: "Нужно заполнить поле",
              validate: (value) =>
                field.key !== "email" ||
                EMAIL_PATTERN.test(String(value)) ||
                "Похоже, это не email",
            }}
          />
        ))}
      </div>

      <div className="flex flex-col gap-4">
        <p className="font-bold">Юридический адрес</p>

        {LOCALES.map((locale) => (
          <Controller
            key={locale.key}
            control={control}
            name={`address.${locale.key}`}
            render={({ fieldState, field: controlled }) => (
              <TextField
                isInvalid={!!fieldState.error}
                value={controlled.value ?? ""}
                onBlur={controlled.onBlur}
                onChange={controlled.onChange}
              >
                <Label>{locale.title}</Label>
                <TextArea
                  ref={controlled.ref}
                  placeholder="г. Алматы, ул. Аспандиярова, д 4"
                  rows={2}
                />
                <FieldError>{fieldState.error?.message}</FieldError>
              </TextField>
            )}
            rules={{ required: "Нужно заполнить поле" }}
          />
        ))}
      </div>

      <div className="flex flex-row items-center gap-3">
        <Button
          isDisabled={!isDirty}
          isPending={isPending}
          type="submit"
          variant="primary"
        >
          Сохранить
        </Button>

        {savedAt && !isDirty && (
          <span className="text-sm text-default-500">
            Сохранено в {savedAt}
          </span>
        )}
      </div>
    </Form>
  );
}
